
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { useProductSubmission } from "@/hooks/useProductSubmission";
import { BaseProductFields } from "./BaseProductFields";
import { PoultryFormFields } from "./PoultryFormFields";
import { 
  AlertDialog,
  AlertDialogContent, 
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { HelpCircle, AlertCircle } from "lucide-react";
import { useState } from "react";

interface AddProductFormProps {
  onSubmit: (product: any) => void;
  onCancel: () => void;
  isPoultry?: boolean;
}

export const AddProductForm = ({
  onSubmit,
  onCancel,
  isPoultry = false
}: AddProductFormProps) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState("Pièce");
  
  const defaultValues = isPoultry ? {
    Race: "",
    Age: "",
    Santé: "Bonne",
    Poids: "",
    Régime: "",
    quantity: "",
    unit: "Tête",
    customUnit: "",
    price: "",
    minQuantity: "",
    purchase_price: ""
  } : {
    name: "",
    quantity: "",
    unit: "Pièce",
    customUnit: "",
    price: "",
    minQuantity: "",
    purchase_price: ""
  };

  const form = useForm({
    defaultValues
  });

  const { handleSubmit: handleProductSubmit, isLoading } = useProductSubmission((product) => {
    onSubmit(product);
    form.reset();
  });
  
  const { watch } = form;
  const unit = watch("unit");
  const customUnit = watch("customUnit");
  const race = watch("Race"); // Ajout de la surveillance du champ Race
  const purchase_price = watch("purchase_price"); // Ajout pour surveiller le prix d'achat

  const onFormSubmit = async (data: any) => {
    if (isPoultry && !data.Race) {
      // Vérifier que la Race est remplie pour les volailles
      form.setError("Race", { 
        type: "manual", 
        message: "La race de la volaille est obligatoire" 
      });
      return;
    }

    const finalUnit = unit === "custom" ? customUnit : unit;
    const finalData = {
      ...data,
      unit: finalUnit,
      price: parseFloat(data.price),
      minQuantity: parseInt(data.minQuantity),
      quantity: parseInt(data.quantity),
      purchase_price: parseFloat(data.purchase_price || "0"),
      status: parseInt(data.quantity) > 0 ? "En stock" : "Rupture"
    };

    if (isPoultry) {
      finalData.name = data.Race; // Utiliser Race comme nom
    }

    console.log("Submitting data with purchase price:", finalData.purchase_price);

    try {
      const success = await handleProductSubmit(
        finalData.name,
        finalData.quantity,
        finalData.unit,
        finalData.customUnit,
        finalData.price,
        finalData.minQuantity,
        isPoultry,
        isPoultry ? {
          Age: finalData.Age,
          Santé: finalData.Santé,
          Poids: finalData.Poids,
          Régime: finalData.Régime,
          Race: finalData.Race
        } : undefined,
        finalData.purchase_price.toString()
      );

      if (success) {
        form.reset();
      }
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
    }
  };

  const showUnitHelp = () => {
    setIsHelpOpen(true);
  };

  return (
    <div className="space-y-6">
      <Alert className="bg-blue-50 border-blue-200">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-700">
          {isPoultry ? (
            "Pour ajouter une volaille, veuillez remplir tous les champs obligatoires, en particulier la race."
          ) : (
            unit === "Pièce" || unit === "Tête" ? (
              "Pour une pièce individuelle, incluez tous les détails dans le nom (taille, couleur, etc.)"
            ) : (
              "Pour un carton/lot/sac, commencez par la quantité puis ajoutez les détails (ex: 12 Chemises Blanches Taille M)"
            )
          )}
        </AlertDescription>
      </Alert>

      <form onSubmit={form.handleSubmit(onFormSubmit)} className="space-y-6">
        {form.formState.errors.Race && (
          <Alert variant="destructive">
            <AlertDescription>
              {form.formState.errors.Race.message}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            Besoin d'aide pour nommer vos produits ?
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="flex items-center gap-1"
            onClick={showUnitHelp}
          >
            <HelpCircle className="h-4 w-4" />
            Voir des exemples
          </Button>
        </div>

        {isPoultry ? (
          <PoultryFormFields form={form} />
        ) : (
          <BaseProductFields 
            form={form}
            onUnitChange={(value) => {
              setSelectedUnit(value);
              form.setValue("unit", value);
            }}
          />
        )}

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Ajout en cours..." : "Ajouter le produit"}
          </Button>
        </div>
      </form>

      <AlertDialog open={isHelpOpen} onOpenChange={setIsHelpOpen}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Comment bien nommer vos produits</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4 text-left">
              <div>
                <h4 className="font-semibold mb-2">Pour l'unité "Pièce" :</h4>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Chemise Polo Bleue, Taille L</li>
                  <li>Jean Slim Noir, Taille 42</li>
                  <li>T-shirt Basic Blanc, Taille M</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Pour l'unité "Carton/Lot/Sac" :</h4>
                <ul className="list-disc pl-4 space-y-1">
                  <li>12 Chemises Blanches Taille M</li>
                  <li>6 T-shirts Noirs Taille L</li>
                  <li>24 Polos Bleus Tailles Assorties (S-XL)</li>
                  <li>10 Jeans Slim Noirs Taille 42</li>
                  <li>8 Chemises Business Blanches Taille XL</li>
                </ul>
              </div>
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-800">
                  Important : Pour les cartons/lots, commencez toujours par indiquer le nombre d'articles, 
                  puis ajoutez les détails. Par exemple : "12 Chemises Blanches Taille M" signifie qu'il y a 
                  12 chemises dans chaque carton.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>J'ai compris</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
